// Comprehensive analytics tracking system for MaxiCar
import api from '../services/api';

// Visit tracking state
let currentVisitId = null;
let pageStartTime = null;
let scrollDepth = 0;
let clickCount = 0;
let userLocation = null;

// Google Analytics integration
export const initGA = () => {
  if (process.env.REACT_APP_ANALYTICS_ID && window.gtag) {
    window.gtag('config', process.env.REACT_APP_ANALYTICS_ID);
  }
};

export const trackPageView = (path) => {
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path
    });
  }
};

export const trackEvent = (category, action, label = null, value = null) => {
  if (window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value
    });
  }
};

// Geolocation tracking
export const getUserLocation = () => {
  return new Promise((resolve) => {
    if (userLocation) {
      resolve(userLocation);
      return;
    }

    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          userLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          resolve(userLocation);
        },
        (error) => {
          console.warn('Geolocation error:', error.message);
          resolve(null);
        },
        {
          timeout: 5000,
          maximumAge: 300000, // Cache for 5 minutes
          enableHighAccuracy: false
        }
      );
    } else {
      console.warn('Geolocation not supported');
      resolve(null);
    }
  });
};

// Enhanced visit tracking functions
export const initVisitTracking = () => {
  // Get user location on init
  getUserLocation().catch(err => console.warn('Failed to get user location:', err));

  // Track scroll depth
  let maxScroll = 0;
  const trackScroll = () => {
    const scrollPercent = Math.round(
      (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
    );
    if (scrollPercent > maxScroll) {
      maxScroll = scrollPercent;
      scrollDepth = Math.min(maxScroll, 100);
    }
  };

  // Track clicks
  const trackClicks = () => {
    clickCount++;
  };

  // Add event listeners
  window.addEventListener('scroll', trackScroll, { passive: true });
  document.addEventListener('click', trackClicks);

  // Track page visibility changes
  document.addEventListener('visibilitychange', () => {
    if (document.hidden && currentVisitId) {
      trackPageExit();
    }
  });

  // Track page unload
  window.addEventListener('beforeunload', () => {
    if (currentVisitId) {
      trackPageExit();
    }
  });

  return () => {
    window.removeEventListener('scroll', trackScroll);
    document.removeEventListener('click', trackClicks);
  };
};

export const trackVisitStart = async (pagePath = null) => {
  try {
    // Reset tracking variables
    pageStartTime = Date.now();
    scrollDepth = 0;
    clickCount = 0;

    // Get user location
    const location = await getUserLocation();

    const visitData = {
      page_url: window.location.href,
      page_path: pagePath || window.location.pathname,
      page_title: document.title,
      referrer: document.referrer
    };

    // Add location data if available
    if (location) {
      visitData.latitude = location.latitude;
      visitData.longitude = location.longitude;
    }

    const response = await api.post('/analytics/track-visit/', visitData);
    
    if (response.data.status === 'success') {
      currentVisitId = response.data.visit_id;
    }

    // Also track with Google Analytics
    trackPageView(visitData.page_path);

  } catch (error) {
    console.warn('Failed to track visit:', error);
  }
};

export const trackPageExit = async () => {
  if (!currentVisitId || !pageStartTime) return;

  try {
    const timeOnPage = Math.round((Date.now() - pageStartTime) / 1000);

    await api.post('/analytics/track-exit/', {
      visit_id: currentVisitId,
      time_on_page: timeOnPage,
      scroll_depth: scrollDepth,
      clicks_count: clickCount
    });

    // Reset tracking variables
    currentVisitId = null;
    pageStartTime = null;
    scrollDepth = 0;
    clickCount = 0;

  } catch (error) {
    console.warn('Failed to track page exit:', error);
  }
};

export const trackConversionEvent = async (eventType, eventData = {}) => {
  try {
    const conversionData = {
      event_type: eventType,
      event_data: eventData,
      conversion_path: getConversionPath()
    };

    // Add booking ID if available
    if (eventData.bookingId) {
      conversionData.booking_id = eventData.bookingId;
    }

    // Add event value if available
    if (eventData.value) {
      conversionData.event_value = eventData.value;
    }

    // Add location data if available in eventData
    if (eventData.latitude && eventData.longitude) {
      conversionData.latitude = eventData.latitude;
      conversionData.longitude = eventData.longitude;
    }

    await api.post('/analytics/track-conversion/', conversionData);

    // Also track with Google Analytics
    trackEvent('Conversion', eventType, eventData.label, eventData.value);

  } catch (error) {
    console.warn('Failed to track conversion:', error);
  }
};

// Get conversion path (pages visited in this session)
const getConversionPath = () => {
  const path = sessionStorage.getItem('conversion_path');
  return path ? JSON.parse(path) : [];
};

// Update conversion path
export const updateConversionPath = (pagePath) => {
  const path = getConversionPath();
  path.push({
    page: pagePath,
    timestamp: Date.now()
  });
  
  // Keep only last 10 pages
  if (path.length > 10) {
    path.shift();
  }
  
  sessionStorage.setItem('conversion_path', JSON.stringify(path));
};

// Specific tracking functions for MaxiCar events
export const trackBookingAttempt = (serviceType = null) => {
  trackEvent('Booking', 'Attempt', 'Booking Form');
  trackConversionEvent('booking_started', {
    label: 'Booking Form Started',
    service_type: serviceType
  });
};

export const trackBookingSuccess = (bookingId, serviceType, value = null) => {
  trackEvent('Booking', 'Success', 'Booking Completed', value);
  trackConversionEvent('booking_completed', {
    label: 'Booking Completed',
    bookingId: bookingId,
    service_type: serviceType,
    value: value
  });
};

export const trackServiceView = (serviceName) => {
  trackEvent('Service', 'View', serviceName);
  trackConversionEvent('service_viewed', {
    label: serviceName,
    service_name: serviceName
  });
};

export const trackContactForm = (formType = 'contact') => {
  trackEvent('Contact', 'Form Submit', formType);
  trackConversionEvent('contact_form', {
    label: `${formType} form submitted`,
    form_type: formType
  });
};

export const trackPhoneCall = async (buttonLocation = 'header') => {
  trackEvent('Contact', 'Phone Call', `${buttonLocation} Phone Button`);
  
  // Get user location for tracking
  const location = await getUserLocation();
  
  const eventData = {
    label: 'Phone number clicked',
    location: buttonLocation
  };
  
  if (location) {
    eventData.latitude = location.latitude;
    eventData.longitude = location.longitude;
  }
  
  trackConversionEvent('phone_click', eventData);
};

export const trackCallNowClick = async (buttonLocation = 'unknown', phoneNumber = null) => {
  trackEvent('Contact', 'Call Now', `Call Now - ${buttonLocation}`);
  
  // Get user location for tracking
  const location = await getUserLocation();
  
  const eventData = {
    label: 'Call Now button clicked',
    location: buttonLocation,
    phone_number: phoneNumber
  };
  
  if (location) {
    eventData.latitude = location.latitude;
    eventData.longitude = location.longitude;
  }
  
  trackConversionEvent('call_now_click', eventData);
};

export const trackWhatsAppClick = () => {
  trackEvent('Contact', 'WhatsApp', 'Floating WhatsApp Button');
  trackConversionEvent('phone_click', {
    label: 'WhatsApp clicked',
    location: 'floating_button'
  });
};

export const trackEmailClick = (location = 'unknown') => {
  trackEvent('Contact', 'Email', location);
  trackConversionEvent('email_click', {
    label: 'Email clicked',
    location: location
  });
};

export const trackUserRegistration = (method = 'email') => {
  trackEvent('User', 'Registration', method);
  trackConversionEvent('user_registration', {
    label: 'User registered',
    registration_method: method
  });
};

// Performance tracking
export const trackPageLoadTime = () => {
  if (window.performance && window.performance.timing) {
    const loadTime = window.performance.timing.loadEventEnd - window.performance.timing.navigationStart;
    trackEvent('Performance', 'Page Load Time', window.location.pathname, loadTime);
  }
};

// Error tracking
export const trackError = (error, context = 'unknown') => {
  trackEvent('Error', error.name || 'Unknown Error', context);
  console.error('Tracked error:', error, 'Context:', context);
};